import {dispatchMutationLifecycleEvent, MutationLifecycleEvent} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {
  UpdateIssueInput,
  updateIssueTitleMutation,
  updateIssueTitleMutation$data,
} from './__generated__/updateIssueTitleMutation.graphql'

/**
 * Fired for the optimistic, error and completed phases of
 * `commitUpdateIssueTitleMutation`, so other data layers (e.g. TanStack Query)
 * can keep themselves in sync with data that Relay just wrote/errored/confirmed.
 */
export const UPDATE_ISSUE_TITLE_MUTATION_EVENT = 'update-issue-title-mutation' as const
export type UpdateIssueTitleMutationEvent = MutationLifecycleEvent<
  typeof UPDATE_ISSUE_TITLE_MUTATION_EVENT,
  updateIssueTitleMutation$data
>

export function commitUpdateIssueTitleMutation({
  environment,
  input: {issueId, title},
  onError,
  onCompleted,
}: {
  environment: Environment
  input: {issueId: string; title: string | null}
  onError?: (error: Error) => void
  onCompleted?: (response: updateIssueTitleMutation$data) => void
}) {
  const inputHash: UpdateIssueInput = {
    id: issueId,
  }
  if (title !== null) {
    inputHash.title = title
  }

  const optimisticResponse: updateIssueTitleMutation$data = {
    updateIssue: {
      issue: {
        id: issueId,
        title: title || '',
        titleHTML: escapeOptimisticTitleHTML(title || ''),
      },
    },
  }

  const disposable = commitMutation<updateIssueTitleMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateIssueTitleMutation($input: UpdateIssueInput!) @raw_response_type {
        updateIssue(input: $input) {
          issue {
            id
            title
            titleHTML
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_ISSUE_TITLE_MUTATION_EVENT, {phase: 'error', error}),
      )
      if (onError) onError(error)
    },
    optimisticResponse,
    onCompleted: response => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_ISSUE_TITLE_MUTATION_EVENT, {phase: 'completed', data: response}),
      )
      if (onCompleted) onCompleted(response)
    },
  })

  // Relay applies `optimisticResponse` synchronously as part of `commitMutation`,
  // so it's safe to dispatch the optimistic phase event right after committing.
  dispatchMutationLifecycleEvent(
    new MutationLifecycleEvent(UPDATE_ISSUE_TITLE_MUTATION_EVENT, {phase: 'optimistic', data: optimisticResponse}),
  )

  return disposable
}
